import React, { useEffect, useState, useRef } from 'react';
import { Menu, LogOut, Search, ChevronDown, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface HeaderProps {
  setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const Header = ({ setSidebarOpen }: HeaderProps) => {
  const [userName, setUserName] = useState("Admin");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Component mount hone par LocalStorage se data nikalna
  useEffect(() => {
    const storedUser = localStorage.getItem("sevaa_admin_user");
    if (storedUser) {
      try {
        const userObj = JSON.parse(storedUser);
        if (userObj && userObj.name) {
          setUserName(userObj.name);
        }
      } catch (error) {
        console.error("Error parsing user data", error);
      }
    }
  }, []);

  // Dropdown ke bahar click karne par usko close karne ka logic
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Logout Function
  const handleLogout = () => {
    localStorage.removeItem("sevaa_admin_token");
    localStorage.removeItem("sevaa_admin_user");
    navigate("/n-jio-sevaa");
  };

  const initial = userName.charAt(0).toUpperCase();

  return (
    <header className="bg-white border-b border-gray-200 h-16 flex items-center justify-between px-4 lg:px-6 relative z-30">
      
      {/* 1. Left Section - Hamburger & Greeting */}
      <div className="flex items-center gap-4 w-1/4 lg:w-1/3">
        <button 
          onClick={() => setSidebarOpen(prev => !prev)}
          className="p-2 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 focus:outline-none transition-colors"
        >
          <Menu size={24} />
        </button>
        <h2 className="text-lg lg:text-xl font-semibold text-gray-800 hidden md:block whitespace-nowrap">
          Welcome, <span className="text-orange-600">{userName}</span>
        </h2>
      </div>

      {/* 2. Middle Section - Search Bar */}
      <div className="flex-1 max-w-md px-4 hidden sm:block w-2/4 lg:w-1/3">
        <div className="relative group">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search size={18} className="text-gray-400 group-focus-within:text-orange-500 transition-colors" />
          </div>
          <input 
            type="text" 
            placeholder="Search campaigns, donations..." 
            className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 focus:bg-white outline-none transition-all text-sm text-slate-700 placeholder-slate-400"
          />
        </div>
      </div>
      
      {/* 3. Right Section - Profile Dropdown */}
      <div className="flex items-center justify-end w-1/4 lg:w-1/3 relative" ref={dropdownRef}>
        
        {/* Clickable Profile Trigger */}
        <button 
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="flex items-center gap-2 hover:bg-slate-50 p-1.5 rounded-lg transition-colors focus:outline-none"
        >
          <div className="w-9 h-9 bg-slate-800 rounded-full flex items-center justify-center text-white font-bold shadow-sm border-2 border-slate-200">
            {initial}
          </div>
          <ChevronDown size={16} className={`text-slate-500 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Dropdown Menu */}
        {isDropdownOpen && (
          <div className="absolute top-14 right-0 w-56 bg-white rounded-xl shadow-lg border border-slate-100 py-2 animate-fade-in-up origin-top-right">
            
            {/* User Info Header inside Dropdown */}
            <div className="px-4 py-3 border-b border-slate-100 mb-1">
              <p className="text-sm font-bold text-slate-800 truncate">{userName}</p>
              <p className="text-xs text-slate-500 truncate">admin@sevaa.org</p>
            </div>

            {/* Dropdown Links */}
            <button 
              onClick={() => { setIsDropdownOpen(false); /* Add profile nav logic here */ }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
            >
              <User size={16} />
              My Profile
            </button>
            
            <div className="h-px bg-slate-100 my-1"></div>
            
            <button 
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors font-medium"
            >
              <LogOut size={16} />
              Sign Out
            </button>

          </div>
        )}

      </div>
    </header>
  );
};

export default Header;